import { BaseGraphqlLayer } from '@core/base';
import { DocumentTypes } from '@introspection/documentTypes';
import { IMutation, IQuery, ISteelFabCategoryInput, ISteelFabCategoryType } from '@introspection/index';
import { Injectable } from '@rxdi/core';
import { deepCopy, importQuery } from '@rxdi/graphql-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type Category = ISteelFabCategoryType & { children?: Category[] };

@Injectable()
export class CategoryService extends BaseGraphqlLayer {
  /** Returns only leaf/brand categories that actually have products (filtering done on backend). */
  getCategories(): Observable<Category[]> {
    return this.watchQuery({
      query: 'list-steelfab-categories.query.graphql',
    }).pipe(map((res) => res.data.listSteelFabCategories as Category[]));
  }

  /**
   * Returns the full flat list built into a nested tree.
   * Nodes without a parentId are roots; children are sorted by `order`.
   */
  getCategoryTree(): Observable<Category[]> {
    return this.getCategories().pipe(
      map((flat) => {
        const nodeMap = new Map<string, Category>();
        flat.forEach((c) => nodeMap.set(c.id, { ...c, children: [] }));
        const roots: Category[] = [];
        nodeMap.forEach((cat) => {
          if (cat.parentId) {
            const parent = nodeMap.get(cat.parentId);
            if (parent) {
              parent.children.push(cat);
              parent.children.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            }
          } else {
            roots.push(cat);
          }
        });
        return roots.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      })
    );
  }

  getAdminCategories(): Observable<Category[]> {
    return this.getCategories();
  }

  getCategoryBySlug(slug: string): Observable<Category> {
    return this.query({
      query: 'find-steelfab-category.query.graphql',
      variables: { slug },
    }).pipe(map((res) => res.data.findSteelFabCategoryBySlug as Category));
  }

  createCategory(categoryInput: ISteelFabCategoryInput): Observable<Category> {
    delete categoryInput['logo'];
    return this.mutate({
      mutation: 'create-steelfab-category.mutation.graphql',
      variables: { categoryInput },
      update: (proxy, { data: { createSteelFabCategory } }) => {
        const query = importQuery<DocumentTypes>('list-steelfab-categories.query.graphql');
        const data = proxy.readQuery<IQuery>({
          query,
        });
        if (data) {
          proxy.writeQuery({
            query,
            data: {
              listSteelFabCategories: [...data.listSteelFabCategories, createSteelFabCategory],
            },
          });
        }
      },
    }).pipe(map((res) => res.data.createSteelFabCategory as Category));
  }

  updateCategory(id: string, categoryInput: ISteelFabCategoryInput): Observable<Category> {
    delete categoryInput['logo'];
    return this.mutate({
      mutation: 'update-steelfab-category.mutation.graphql',
      variables: { id, categoryInput },
      update: (proxy, { data: { updateSteelFabCategory } }) => {
        const query = importQuery<DocumentTypes>('list-steelfab-categories.query.graphql');
        const data = deepCopy(
          proxy.readQuery<IQuery>({
            query,
          })
        );
        if (data) {
          const index = data.listSteelFabCategories.findIndex((v) => v.id === updateSteelFabCategory.id);
          const listSteelFabCategories = data.listSteelFabCategories;
          listSteelFabCategories[index] = updateSteelFabCategory;
          proxy.writeQuery({
            query,
            data: {
              listSteelFabCategories,
            },
          });
        }
      },
    }).pipe(map((res) => res.data.updateSteelFabCategory));
  }

  deleteCategory(id: string): Observable<Category> {
    return this.mutate({
      mutation: 'delete-steelfab-category.mutation.graphql',
      variables: { id },
      update: (proxy, { data: { deleteSteelFabCategory } }) => {
        const query = importQuery<DocumentTypes>('list-steelfab-categories.query.graphql');
        const data = proxy.readQuery<IQuery>({
          query,
        });
        if (data) {
          proxy.writeQuery({
            query,
            data: {
              listSteelFabCategories: data.listSteelFabCategories.filter((v) => v.id !== deleteSteelFabCategory.id),
            },
          });
        }
      },
    }).pipe(map((res) => res.data.deleteSteelFabCategory as Category));
  }
}
